// src/pages/CampaignsDashboard/index.jsx
// Campanhas buscadas diretamente do MongoDB via API — sem localStorage como fonte de dados
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChartBarIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import TopPageHeader from '../../ui/PageHeader';
import apiClient from '../../lib/apiClient';
import storage from '../../lib/storage';
import './dashboard.css';

const STATUS_LABEL = {
  draft:   { label: 'Rascunho',   cls: 'mcd-badge--draft'   },
  active:  { label: 'Ativa',      cls: 'mcd-badge--active'  },
  paused:  { label: 'Pausada',    cls: 'mcd-badge--paused'  },
  expired: { label: 'Expirada',   cls: 'mcd-badge--expired' },
};

function MiniBar({ values = [], color }) {
  const max = Math.max(...values, 1);
  return (
    <div className="mcd-minibar">
      {values.map((v, i) => (
        <div
          key={i}
          className="mcd-minibar__bar"
          style={{ height: `${Math.max(4, (v / max) * 36)}px`, background: color }}
          title={v}
        />
      ))}
    </div>
  );
}

function CampaignCard({ camp, onOpen, onClone }) {
  const id       = camp.id;
  const isActive = camp.status === 'active';
  const status   = STATUS_LABEL[camp.status] || { label: camp.status, cls: 'mcd-badge--draft' };

  // Performance já vem embutida na resposta do backend (total_clicks, total_sales, etc.)
  const totalClicks = camp.total_clicks      ?? 0;
  const totalSales  = camp.total_sales       ?? 0;
  const clicks7d    = camp.performance?.clicks ?? [];

  return (
    <div className={`mcd-card ${isActive ? 'mcd-card--active' : ''}`}>
      <div className="mcd-card__head">
        <div className="mcd-card__info">
          <span className={`mcd-badge ${status.cls}`}>{status.label}</span>
          <h3 className="mcd-card__name">{camp.business_name || camp.product_name || 'Campanha'}</h3>
          <p className="mcd-card__sub">{camp.niche || 'Geral'} — {camp.plan_name || camp.plan_id || 'Sem plano'}</p>
        </div>
        <div className="mcd-card__id">#{id.slice(-6).toUpperCase()}</div>
      </div>

      {/* Mini gráfico de cliques (apenas campanhas ativas com dados) */}
      {isActive && clicks7d.length > 0 && (
        <div className="mcd-card__graph">
          <MiniBar values={clicks7d} color="var(--mcd-cyan)" />
          <div className="mcd-card__graph-labels">
            <span>Cliques 7d</span>
            <span className="mcd-card__graph-val">{totalClicks.toLocaleString('pt-BR')}</span>
          </div>
        </div>
      )}

      <div className="mcd-card__kpis">
        <div className="mcd-kpi">
          <span className="mcd-kpi__v">{totalClicks.toLocaleString('pt-BR')}</span>
          <span className="mcd-kpi__l">Cliques</span>
        </div>
        <div className="mcd-kpi mcd-kpi--green">
          <span className="mcd-kpi__v">{totalSales}</span>
          <span className="mcd-kpi__l">Vendas</span>
        </div>
        <div className="mcd-kpi">
          <span className="mcd-kpi__v">{camp.plan_price ? `R$${camp.plan_price}` : '—'}</span>
          <span className="mcd-kpi__l">Plano</span>
        </div>
      </div>

      <div className="mcd-card__actions">
        {isActive ? (
          <button className="mcd-btn-primary" onClick={() => onOpen(id, 'sales')}>
            Ver Dashboard
          </button>
        ) : (
          <button className="mcd-btn-outline" onClick={() => onOpen(id, 'profile')}>
            Continuar
          </button>
        )}
        <button className="mcd-btn-ghost" onClick={() => onClone(id)}>
          Repetir
        </button>
      </div>
    </div>
  );
}

export default function CampaignsDashboardPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [cloning, setCloning]     = useState('');
  const [toast, setToast]         = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3200); };

  // Busca campanhas do MongoDB via API.
  // - Usuário logado   → GET /mp/campaigns (filtra por owner_id no backend)
  // - Usuário anônimo  → GET /mp/campaigns?ids=... (usa IDs guardados no localStorage apenas como referência)
  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const token = storage.get('authToken');
      let url = '/mp/campaigns';

      if (!token) {
        // Fallback anônimo: ainda busca do MongoDB, mas por IDs conhecidos
        const localIds = JSON.parse(localStorage.getItem('mp_campaign_ids') || '[]');
        if (localIds.length === 0) { setCampaigns([]); return; }
        url = `/mp/campaigns?ids=${localIds.join(',')}`;
      }

      const r = await apiClient.request(url, { method: 'GET' });
      const d = await r.json();
      if (d.success) setCampaigns(d.campaigns || []);
    } catch (e) {
      console.error('Erro ao carregar campanhas:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const handleOpen = (id, step = 'profile') => {
    navigate(`/campaign-flow?campaign=${id}&step=${step}`);
  };

  const handleClone = async (id) => {
    setCloning(id);
    try {
      const r = await apiClient.request(`/campaign/${id}/clone`, { method: 'POST' });
      const d = await r.json();
      if (d.success) {
        showToast('Campanha duplicada!');
        await loadCampaigns(); // Recarrega direto do MongoDB
      } else {
        showToast('Erro ao duplicar campanha');
      }
    } catch {
      showToast('Erro ao duplicar campanha');
    } finally {
      setCloning('');
    }
  };

  const filtered = search
    ? campaigns.filter(c =>
        (c.business_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.product_name  || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.niche         || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.id            || '').toLowerCase().includes(search.toLowerCase())
      )
    : campaigns;

  return (
    <div className="mcd-page">
      <TopPageHeader />
      {toast && <div className="mcd-toast">{toast}</div>}

      <div className="mcd-container">
        {/* Hero */}
        <div className="mcd-hero">
          <div className="mcd-hero__text">
            <h1 className="mcd-hero__title">
              <ChartBarIcon style={{width:20,height:20}} /> Minhas Campanhas
            </h1>
            <p className="mcd-hero__sub">Dados em tempo real direto do MongoDB — ordenados do mais recente.</p>
          </div>
          <div className="mcd-hero__actions">
            <button className="mcd-btn-ghost" onClick={loadCampaigns} title="Recarregar">
              Atualizar
            </button>
            <button className="mcd-btn-cta" onClick={() => navigate('/campaign-flow')}>
              Nova Campanha
            </button>
          </div>
        </div>

        {/* Filtro */}
        {campaigns.length > 0 && (
          <div className="mcd-search-row">
            <input
              className="mcd-search"
              placeholder="Buscar por nome, nicho ou ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <span className="mcd-count">{filtered.length} campanha{filtered.length !== 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Estados */}
        {loading ? (
          <div className="mcd-grid">
            {[1, 2, 3].map(i => (
              <div key={i} className="mcd-card mcd-card--loading">
                <div className="mcd-skeleton mcd-skeleton--title" />
                <div className="mcd-skeleton mcd-skeleton--bar" />
              </div>
            ))}
          </div>
        ) : campaigns.length === 0 ? (
          <div className="mcd-empty-state">
            <div className="mcd-empty-state__icon"><RocketLaunchIcon style={{width:40,height:40}} /></div>
            <h2 className="mcd-empty-state__title">Nenhuma campanha ainda</h2>
            <p className="mcd-empty-state__sub">Crie sua primeira campanha com IA em menos de 2 minutos.</p>
            <button className="mcd-btn-cta" onClick={() => navigate('/campaign-flow')}>
              Criar primeira campanha
            </button>
          </div>
        ) : (
          <div className="mcd-grid">
            {filtered.map(camp => (
              <CampaignCard
                key={camp.id}
                camp={camp}
                onOpen={handleOpen}
                onClone={handleClone}
              />
            ))}
          </div>
        )}

        {cloning && <div className="mcd-overlay-msg">Duplicando campanha...</div>}
      </div>
    </div>
  );
}
